/**
 * Подключение к кошельку, который сам подставил провайдер в страницу.
 *
 * Это то, что происходит внутри встроенного браузера кошелька: Trust, Bitget,
 * OKX, TronLink и Phantom кладут в window свой провайдер, и подключение идёт
 * напрямую, без WalletConnect, без relay и без прыжков между приложениями.
 *
 * Почему не через Reown AppKit. Внутри кошелька AppKit всё равно откроет своё
 * окно со списком кошельков, и человеку придётся выбрать тот, в котором он уже
 * находится, — лишний шаг ровно там, где выбор уже сделан. Плюс AppKit для
 * Solana не работает с Phantom по WalletConnect (у Phantom его нет), поэтому
 * на прямом провайдере путь короче и надёжнее в обеих сетях.
 *
 * Провайдер появляется не мгновенно: кошелёк вставляет его в среднем через
 * 100–800 мс после загрузки страницы, иногда позже. Отсюда ожидание.
 */

import { connectTron, detectTronWalletLabel } from './tronBrowser';

export type WalletFamily = 'evm' | 'solana' | 'tron';

export interface InjectedConnection {
  address: string;
  walletLabel: string;
}

interface InjectedEvmProvider {
  request(args: { method: string; params?: unknown[] | object }): Promise<unknown>;
  isTrust?: boolean;
  isTrustWallet?: boolean;
  isBitKeep?: boolean;
  isBitget?: boolean;
  isOkxWallet?: boolean;
  isMetaMask?: boolean;
  isTokenPocket?: boolean;
  providers?: InjectedEvmProvider[];
}

interface InjectedSolanaProvider {
  connect(opts?: { onlyIfTrusted?: boolean }): Promise<{ publicKey?: { toString(): string } }>;
  publicKey?: { toString(): string } | null;
  isPhantom?: boolean;
  isSolflare?: boolean;
  isTrust?: boolean;
  isBitKeep?: boolean;
  isOkxWallet?: boolean;
}

declare global {
  interface Window {
    // window.ethereum здесь не объявляем: его уже объявляет типизация
    // кошельковых библиотек как Record<string, unknown>, и второе объявление
    // с другим типом ломает сборку. Сужаем тип в месте доступа.
    solana?: InjectedSolanaProvider;
    solflare?: InjectedSolanaProvider;
    phantom?: { solana?: InjectedSolanaProvider };
    okxwallet?: { solana?: InjectedSolanaProvider };
    bitkeep?: { solana?: InjectedSolanaProvider; ethereum?: InjectedEvmProvider };
    trustwallet?: { solana?: InjectedSolanaProvider };
  }
}

export function injectedEvmProvider(): InjectedEvmProvider | null {
  if (typeof window === 'undefined') return null;
  const candidate = window.bitkeep?.ethereum || (window as any).ethereum;
  return candidate && typeof candidate.request === 'function' ? (candidate as InjectedEvmProvider) : null;
}

export function injectedSolanaProvider(): InjectedSolanaProvider | null {
  if (typeof window === 'undefined') return null;
  return (
    window.phantom?.solana ||
    window.solflare ||
    window.okxwallet?.solana ||
    window.bitkeep?.solana ||
    window.trustwallet?.solana ||
    window.solana ||
    null
  );
}

export function hasInjectedProvider(family: WalletFamily): boolean {
  if (typeof window === 'undefined') return false;
  if (family === 'tron') return Boolean(window.tronLink);
  if (family === 'solana') return Boolean(injectedSolanaProvider());
  return Boolean(injectedEvmProvider());
}

/** Дождаться, пока кошелёк вставит провайдер в страницу. */
export async function waitForInjectedProvider(family: WalletFamily, timeoutMs = 8000): Promise<boolean> {
  const deadline = Date.now() + timeoutMs;
  while (!hasInjectedProvider(family)) {
    if (Date.now() >= deadline) return false;
    await new Promise((resolve) => setTimeout(resolve, 150));
  }
  return true;
}

function evmLabel(provider: InjectedEvmProvider): string {
  if (provider.isTrust || provider.isTrustWallet) return 'Trust Wallet';
  if (provider.isBitKeep || provider.isBitget) return 'Bitget Wallet';
  if (provider.isOkxWallet) return 'OKX Wallet';
  if (provider.isTokenPocket) return 'TokenPocket';
  if (provider.isMetaMask) return 'MetaMask';
  return 'Wallet';
}

function solanaLabel(provider: InjectedSolanaProvider): string {
  if (provider.isPhantom) return 'Phantom';
  if (provider.isSolflare) return 'Solflare';
  if (provider.isTrust) return 'Trust Wallet';
  if (provider.isBitKeep) return 'Bitget Wallet';
  if (provider.isOkxWallet) return 'OKX Wallet';
  return 'Solana Wallet';
}

function isUserRejection(err: any): boolean {
  const code = err?.code;
  const message = String(err?.message || err || '');
  return code === 4001 || code === -32603 || /reject|denied|cancel|declined|closed/i.test(message);
}

/**
 * Подключиться к встроенному провайдеру. Бросает 'wallet_not_injected', если
 * кошелёк так и не появился, и 'user_rejected', если человек отказал.
 */
export async function connectInjected(family: WalletFamily): Promise<InjectedConnection> {
  const present = await waitForInjectedProvider(family);
  if (!present) {
    throw new Error('wallet_not_injected');
  }

  try {
    if (family === 'tron') {
      const wallet = await connectTron();
      return { address: wallet.address, walletLabel: wallet.walletLabel || detectTronWalletLabel() };
    }

    if (family === 'solana') {
      const provider = injectedSolanaProvider();
      if (!provider) throw new Error('wallet_not_injected');
      const res = await provider.connect();
      const address = res?.publicKey?.toString() || provider.publicKey?.toString() || '';
      if (!address) throw new Error('no_address');
      return { address, walletLabel: solanaLabel(provider) };
    }

    const provider = injectedEvmProvider();
    if (!provider) throw new Error('wallet_not_injected');
    const accounts = (await provider.request({ method: 'eth_requestAccounts' })) as string[];
    const address = Array.isArray(accounts) ? accounts[0] : '';
    if (!address) throw new Error('no_address');
    return { address, walletLabel: evmLabel(provider) };
  } catch (err: any) {
    if (isUserRejection(err)) {
      throw new Error('user_rejected');
    }
    throw err instanceof Error ? err : new Error(String(err));
  }
}
